import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback } from 'react';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { useMutation } from 'react-query';

import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { REVALIDATE_MODES } from '@savgroup-front-common/constants';
import { useToasts } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import useStepOrchestratorNextStep from '@savgroup-front-common/core/molecules/StepsOrchestrator/hooks/useStepOrchestratorNextStep';
import { OwnerService } from '@/api';
import { ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE } from '@/api/Owner/getOrderByOrderReferenceAndOrderPrice';
import { CreateClaimContext, CreateClaimValues } from '@/types';

import messages from '../messages';

import OnlyWithOrderReferenceSchema from './PurchaseDateLoginEnable.schema';
import { PurchaseDateLoginEnableValues } from './PurchaseDateLoginEnable.types';

const GetOrderByOrderReferenceAndOrderPrice =
  'getOrderByOrderReferenceAndOrderPrice';

interface UsePurchaseDateLoginEnableAgrs {
  values: CreateClaimValues;
}

interface UseCustomerInvoiceInformationsReturnValues {
  formContext: UseFormReturn<PurchaseDateLoginEnableValues>;
  isLoading: boolean;
  onSubmit: SubmitHandler<PurchaseDateLoginEnableValues>;
}

const usePurchaseDateLoginEnable = ({
  values,
}: UsePurchaseDateLoginEnableAgrs): UseCustomerInvoiceInformationsReturnValues => {
  const { pushErrors } = useToasts({
    extendedMessages: messages,
  });
  const { sellerId } = values;
  const nextStep = useStepOrchestratorNextStep<
    CreateClaimValues,
    CreateClaimContext
  >();

  const formContext = useForm<PurchaseDateLoginEnableValues>({
    resolver: yupResolver(OnlyWithOrderReferenceSchema),
    mode: REVALIDATE_MODES.ON_CHANGE,
    defaultValues: {
      orderReference: values.orderReference || '',
    },
  });

  const { watch } = formContext;

  const orderReference = watch('orderReference');
  const purchaseDate = watch('purchaseDate');

  const { mutateAsync: handleSubmitOrderInformations, isLoading } = useMutation(
    [
      GetOrderByOrderReferenceAndOrderPrice,
      { sellerId, orderReference, purchaseDate },
    ],
    async () => {
      if (!sellerId || !orderReference) {
        return undefined;
      }

      const response = await OwnerService.getOrderByOrderReferenceAndOrderPrice(
        {
          orderReference,
          purchaseDate,
          sellerId,
        },
      );

      if (response.failure) {
        pushErrors(response.errors);

        logError(
          `Error with orderReference ${response.errors.map((e) => e.message)}`,
        );

        return undefined;
      }

      return response.value;
    },
  );

  const onSubmit: SubmitHandler<PurchaseDateLoginEnableValues> =
    useCallback(async () => {
      const response = await handleSubmitOrderInformations();

      if (
        response?.importStatus ===
        ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE.ORDER_ALREADY_IMPORT
      ) {
        window.location = response?.irshUrl as unknown as string & Location;

        return undefined;
      }
      if (
        response?.importStatus ===
        ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE.CUSTOMER_IDENTIFIED
      ) {
        nextStep({
          ...values,
          orderReference,
          purchaseDate,
          owner: values.owner ? values.owner : response?.owner,
        });

        return undefined;
      }
      if (
        response?.importStatus ===
        ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE.ANONYMOUS_CUSTOMER
      ) {
        nextStep({
          ...values,
          orderReference,
        });

        return undefined;
      }

      return undefined;
    }, [
      handleSubmitOrderInformations,
      orderReference,
      nextStep,
      values,
      purchaseDate,
    ]);

  return {
    formContext,
    isLoading,
    onSubmit,
  };
};

export default usePurchaseDateLoginEnable;
